// pages/TopicDetail.js
import { useParams, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import Breadcrumbs from '../components/Breadcrumbs';

/** ---------- Helpers for rendering six_achievements JSON ---------- */
function useParsedSix(jsonOrText) {
  return useMemo(() => {
    if (!jsonOrText) return null;
    try {
      const parsed = typeof jsonOrText === 'string' ? JSON.parse(jsonOrText) : jsonOrText;
      if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.blocks)) return null;
      return parsed;
    } catch {
      return null;
    }
  }, [jsonOrText]);
}

function SixBlock({ block }) {
  if (!block || typeof block !== 'object') return null;

  if (block.type === 'heading') {
    const lvl = Math.min(6, Math.max(2, Number(block.level) || 3));
    const Tag = `h${lvl}`;
    return (
      <Tag className="six-h" style={{ margin: '.75rem 0 .35rem' }}>
        {block.text}
      </Tag>
    );
  }

  if (block.type === 'paragraph') {
    return (
      <p className="six-p" style={{ margin: '.35rem 0', lineHeight: 1.6 }}>
        {block.text}
      </p>
    );
  }

  if (block.type === 'list' && Array.isArray(block.items)) {
    const ListTag = block.ordered ? 'ol' : 'ul';
    return (
      <ListTag className="six-list" style={{ margin: '.35rem 1.25rem', lineHeight: 1.6 }}>
        {block.items.map((it, i) => <li key={i}>{it}</li>)}
      </ListTag>
    );
  }

  return null;
}

function SixAchievementsView({ jsonText }) {
  const data = useParsedSix(jsonText);

  if (!data) {
    return <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{jsonText}</pre>;
  }

  const {
    title,
    authors,
    created_at,
    tags,
    blocks = [],
    metadata = {},
  } = data;

  return (
    <div className="six-root" style={{ display: 'grid', gap: '.5rem' }}>
      {(title || (authors && authors.length) || created_at || (tags && tags.length)) && (
        <header className="six-meta" style={{ display: 'grid', gap: '.4rem' }}>
          {title && (
            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{title}</h3>
          )}
          {(authors?.length || created_at) && (
            <div className="six-byline" style={{ fontSize: '.9rem', color: 'var(--muted)' }}>
              {authors?.length ? `מאת ${authors.join(', ')}` : null}
              {authors?.length && created_at ? ' · ' : null}
              {created_at ? `נוצר ב־${created_at}` : null}
            </div>
          )}
          {tags?.length ? (
            <div className="six-tags" style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
              {tags.map((t, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '.8rem',
                    padding: '.15rem .45rem',
                    borderRadius: '999px',
                    border: '1px solid #cbd5e1',
                    background: 'var(--surface)',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          ) : null}
        </header>
      )}

      {Object.keys(metadata || {}).length > 0 && (
        <div className="six-metadata" style={{ fontSize: '.85rem', color: 'var(--muted)' }}>
          {metadata.subject && <span>נושא: {metadata.subject}</span>}
          {metadata.subject && (metadata.grade_level || metadata.source || metadata.tanakh_source) ? ' · ' : null}
          {metadata.grade_level && <span>כיתה: {metadata.grade_level}</span>}
          {metadata.grade_level && (metadata.source || metadata.tanakh_source) ? ' · ' : null}
          {metadata.source && <span>מקור: {metadata.source}</span>}
          {metadata.source && metadata.tanakh_source} {metadata.tanakh_source && <span>תנ״ך: {metadata.tanakh_source}</span>}
        </div>
      )}

      <section className="six-content">
        {blocks.map((b, i) => <SixBlock key={i} block={b} />)}
      </section>
    </div>
  );
}
/** ----------------------------------------------------------------- */

export default function TopicDetail() {
  const { id, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const urlDomain = (searchParams.get('domain') || 'toshba').toLowerCase();
  const chapter = searchParams.get('chapter') || '';

  const [tractateName, setTractateName] = useState(location.state?.tractateName || '');
  const [topic, setTopic] = useState(null);
  const [counts, setCounts] = useState({});
  const [sixFile, setSixFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // fetch tractate name once if not provided via state
  useEffect(() => {
    if (tractateName) return;
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`/api/tractates/${id}/`);
        if (!res.ok) throw new Error('failed tractate');
        const data = await res.json();
        if (!ignore) setTractateName(data?.name || '');
      } catch {/* silent */} finally {
        // no separate nameLoading state; overall loader covers it
      }
    })();
    return () => { ignore = true; };
  }, [id, tractateName]);

  // decide which cards to show by domain
  const categories = useMemo(() => {
    const base = [
      { key: 'PREP',          label: 'הכנת הסוגיה',     slug: 'preparation', asset: 'prep'  },
      { key: 'TOOLS',         label: 'כלים להוראה',     slug: 'tools',       asset: 'tools' },
      { key: 'EVAL',          label: 'הערכה ומשחוק',    slug: 'evaluation',  asset: 'eval'  },
      { key: 'VISUAL_TALMUD', label: 'אמצעים חזותיים - תרשימים, תמונות וסרטונים',     slug: 'visual',      asset: 'visual' },
    ];
    if ((topic?.domain || urlDomain) === 'talmud') {
      base.push({ key: 'TOSFOT_IYYUN', label: 'תוספות וסוגיות בעיון', slug: 'tosafot', asset: 'tosafot' });
    }
    return base;
  }, [topic?.domain, urlDomain]);

  // load topic + per-category counts + first SIX_ACHIEVEMENTS file
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const topicRes = await fetch(`/api/topics/${topicId}/`);
        if (!topicRes.ok) throw new Error('failed topic');
        const topicData = await topicRes.json();

        const reqs = categories.map(c =>
          fetch(`/api/topics/${topicId}/files/?category=${encodeURIComponent(c.key)}`)
        );
        // also fetch the six achievements file for the download button
        reqs.push(fetch(`/api/topics/${topicId}/files/?category=SIX_ACHIEVEMENTS`));

        const responses = await Promise.all(reqs);
        if (responses.some(r => !r.ok)) throw new Error('failed files');

        const arrays = await Promise.all(responses.map(r => r.json()));
        const nextCounts = {};
        categories.forEach((c, idx) => { nextCounts[c.key] = arrays[idx].length; });

        const sixArr = arrays[arrays.length - 1];
        const sixFirst = Array.isArray(sixArr) && sixArr.length > 0 ? sixArr[0] : null;

        if (!ignore) {
          setTopic(topicData);
          setCounts(nextCounts);
          setSixFile(sixFirst);
        }
      } catch (e) {
        if (!ignore) setError('שגיאה בטעינת פרטי הסוגיה');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [topicId, categories]);

  if (loading) return <div className="container"><p className="muted">טוען…</p></div>;
  if (error)   return <div className="container"><p style={{ color: 'crimson' }}>{error}</p></div>;
  if (!topic)  return null;

  const domain = topic.domain || urlDomain;

  // Build tail for links (preserve domain & chapter)
  const tail = `?domain=${domain}${chapter ? `&chapter=${encodeURIComponent(chapter)}` : ''}`;
  const topicsHref = `/tractates/${id}/topics${tail}`;

  // Chapter label (prefer chapter title; fallback to number)
  const chapterTitle =
    (topic?.chapter && (topic.chapter.title?.trim()
      || (topic.chapter.number != null ? `פרק ${topic.chapter.number}` : '')))
    || '';

  const hrefFor = (c) => {
    const base = `/tractates/${id}/topics/${topicId}`;
    if (c.key === 'PREP')          return `${base}/preparation${tail}`;
    if (c.key === 'TOOLS')         return `${base}/tools${tail}`;
    if (c.key === 'EVAL')          return `${base}/evaluation${tail}`;
    if (c.key === 'VISUAL_TALMUD') return `${base}/visual${tail}`;
    if (c.key === 'TOSFOT_IYYUN')  return `${base}/tosafot${tail}`;
    return `#`;
  };

  // utils
  const sanitize = (s) => (s || '')
    // strip risky characters for filenames
    .replace(/[\\/:*?"<>|]+/g, '')
    .trim();

  // build a predictable download name with extension
  function downloadNameFrom(urlStr, preferredTitle) {
    try {
      const u = new URL(urlStr, window.location.origin);
      const base = decodeURIComponent(u.pathname.split('/').pop() || '');
      const ext  = base.includes('.') ? base.split('.').pop() : '';  // e.g. 'docx' / 'pdf'
      const cleanTitle = sanitize(preferredTitle || base.replace(/\.[^.]+$/, '') || 'download');
      return ext ? `${cleanTitle}.${ext}` : cleanTitle;
    } catch {
      return sanitize(preferredTitle || 'download');
    }
  }

  // then in TopicDetail.jsx:
  const sixDownloadName = downloadNameFrom(sixFile?.file, sixFile?.name);

  // ---- Domain-aware breadcrumb label for the tractate ----
  const domText = domain === 'talmud' ? 'תלמוד' : 'תושב"ע';
  const tractateCrumbLabel = `${tractateName ? `${tractateName} (${domText})` : 'מסכת'}`;
  const tractatesHref = `/tractates?domain=${domain}`;

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: tractatesHref, label: 'מסכתות' },
          { to: `/tractates/${id}?domain=${domain}`, label: tractateCrumbLabel },
          { to: topicsHref, label: chapterTitle ? `סוגיות ${chapterTitle}` : 'סוגיות' },
          { label: topic.name || '...' },
        ]}
      />

      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>{topic.name}</h2>
      </div>

      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to={`/tractates/${id}?domain=${domain}`} state={{ tractateName }}>
          חזרה למסכת
        </Link>
      </p>

      {(topic.summary || topic.six_achievements) && (
        <div className="stack-3" style={{ marginTop: '1rem' }}>
          {topic.summary && (
            <details className="accordion accordion--assistant">
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                תקציר הסוגיה
              </summary>
              <div className="accordion__content">
                <pre className="accordion__pre">{topic.summary}</pre>
              </div>
            </details>
          )}

          {topic.six_achievements && (
            <details className="accordion accordion--assistant">
              <summary
                style={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '.75rem',
                }}
              >
                <span className="acc-title">ששת ההישגים</span>
                <span className="acc-spacer" />
                {sixFile?.file && (
                  <a
                    className="btn btn--sm"
                    href={sixFile.file}
                    target="_blank"
                    rel="noreferrer"
                    download={sixDownloadName}
                    aria-label="הורדת קובץ ששת ההישגים"
                  >
                    הורדה
                  </a>
                )}
              </summary>
              <div className="accordion__content">
                <SixAchievementsView jsonText={topic.six_achievements} />
              </div>
            </details>
          )}
        </div>
      )}


      <div className="stack-3" style={{ marginTop: '1.25rem' }}>
        <p className="muted">בחרו את סוג חומרי הלימוד:</p>
      </div>

      <div className="grid-autofit" style={{ marginTop: '1rem' }}>
        {categories.map((c) => {
          const to = hrefFor(c);
          return (
            <article key={c.key} className="c-card">
              <Link to={to} aria-label={c.label} state={{ tractateName, topicName: topic.name }}>
                <img
                  className="c-card__media"
                  src={`/assets/topics/${c.asset}.webp`}
                  alt={c.label}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} // optional: hide if missing
                />
              </Link>
              <div className="c-card__body">
                <h3 className="c-card__title">
                  <Link to={to} state={{ tractateName, topicName: topic.name }}>
                    {c.label}
                  </Link>
                </h3>
                <p className="c-card__meta">{counts[c.key] ?? 0} קבצים</p>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
